import fs from 'node:fs';
import path from 'node:path';
import { fileURLToPath } from 'node:url';
import { materializeDesign } from './design.js';
import { isUtf8Text, substituteString, type TemplateTokens } from './substitute.js';
import { defaultTheme, type ScaffoldTheme } from './theme.js';

/** The {{GHOSTY_*}} values to inject into the templates. */
export type ScaffoldTokens = TemplateTokens;

/**
 * Who can reach the deployed app:
 *  - 'public'  — anyone with the URL (today's default; gate is a pass-through).
 *  - 'invite'  — visitors need the app's invite link/code once; a signed cookie
 *                remembers them. The code lives in the INVITE_CODE env var.
 *  - 'members' — end users must sign in as members of the workspace that owns
 *                the app; the gate's signing key lives in ACCESS_GATE_SECRET.
 *  - 'byo'     — the platform injects NO gating; the scaffold ships a stub the
 *                builder replaces with their own authn/authz.
 */
export type AccessMode = 'public' | 'invite' | 'members' | 'byo';

/**
 * A single scaffolded file, ready to write to disk or feed into the
 * admin-app provision manifest. UTF-8 text rides as a string with tokens
 * already substituted; binary content rides base64-encoded and untouched.
 */
export interface ScaffoldFile {
  /** Posix-style path relative to the app root, e.g. "backend/src/index.ts". */
  path: string;
  /** Decoded text (utf8) or raw bytes (base64) per `encoding`. */
  content: string;
  encoding: 'utf8' | 'base64';
}

/**
 * Per-app values used to MATERIALISE a complete app.config.json — the file
 * every `ghosty` command reads. Passed by both producers (admin-app's web
 * create flow and the CLI's `ghosty init`) so the downloaded zip and a local
 * scaffold emit an identical, valid config.
 */
export interface ScaffoldAppConfig {
  slug: string;
  name: string;
  description: string;
  /** Real boolean — set structurally, never via {{GHOSTY_*}} string tokens. */
  database: boolean;
  /** Private per-app file storage. Same structural-boolean rule as database.
   *  Optional for older callers — omitted reads as false. */
  fileStorage?: boolean;
  backend: 'node' | 'python';
  /** The stamp's admin URL (lets the CLI discover where to sign in). */
  adminUrl: string;
  /** The company/tenant slug. */
  company: string;
  /** End-user access mode baked into app.config.json. Defaults to 'public'. */
  accessMode?: AccessMode;
}

export interface ScaffoldOptions {
  /** Selects the backend-node | backend-python layer (lands in backend/). */
  backend: 'node' | 'python';
  /**
   * Whether the app uses a database. Carried through for callers/manifests;
   * it does NOT change which template files are emitted (the CLI's behavior).
   */
  database: boolean;
  /** {{GHOSTY_*}} substitution values. */
  tokens: ScaffoldTokens;
  /**
   * End-user access mode. 'invite' and 'byo' overlay the backend's accessGate
   * module with the mode's variant (templates/access-{backend}-{mode});
   * 'public' (the default) keeps the pass-through gate the backend layer ships.
   */
  accessMode?: AccessMode;
  /**
   * The tenant's FULL derived brand theme — the design-token set (light or
   * dark), font stacks, Google Fonts URL, and optional logo. After the normal
   * {{GHOSTY_*}} substitution, this is MATERIALISED into the four theme-bearing
   * frontend files (index.css, design-system/tokens.json, index.html,
   * tailwind.config.ts) so the rendered app uses the full palette + fonts.
   *
   * Optional for back-compat: when omitted, Ghosty's house brand (DEFAULT_BRAND)
   * is derived and baked, so a scaffold is always fully branded.
   */
  theme?: ScaffoldTheme;
  /**
   * Per-app values to bake into app.config.json. When provided, a
   * deterministic post-pass REPLACES the template's `{secrets,env}` stub with
   * the full config (slug/name/description/database/backend + platform.
   * {adminUrl,company}), preserving the stub's secrets/env defaults. Omitted by
   * older callers/tests — then the stub is left untouched (back-compat).
   */
  appConfig?: ScaffoldAppConfig;
  /**
   * Override the templates root. Defaults to this package's bundled
   * templates/ via templatesDir(). The published CLI passes its OWN bundled
   * copy here (the scaffolder package isn't present in the packed CLI).
   */
  templatesDir?: string;
}

/** One template layer copied into the scaffold (later layers win conflicts). */
interface ScaffoldLayer {
  /** Directory name under templatesDir(), e.g. "common" or "backend-node". */
  src: string;
  /** Subdir of the app root to land under ("" = root). */
  dest: string;
}

/**
 * Resolve the package's bundled templates/ directory.
 *
 * tsc preserves the src/ tree shape into dist/, so this module sits at the
 * same depth in both forms:
 *   <packageRoot>/src/scaffold.ts   (dev / vitest)
 *   <packageRoot>/dist/scaffold.js  (built / installed)
 *   <packageRoot>/templates/        (shipped via package.json "files")
 * → one level up from this module, then ./templates.
 *
 * NOTE: the published CLI bundles this engine and ships its OWN templates
 * copy; it does NOT call templatesDir() at runtime (see CLI's scaffold.ts).
 */
export function templatesDir(): string {
  return path.resolve(path.dirname(fileURLToPath(import.meta.url)), '..', 'templates');
}

/** The layered template tree the CLI assembles, mirrored precisely. */
function layersFor(backend: 'node' | 'python', accessMode: AccessMode): ScaffoldLayer[] {
  const backendLayer = backend === 'python' ? 'backend-python' : 'backend-node';
  return [
    { src: 'common', dest: '' },
    // Backend code lives in backend/ (the Dockerfile contract).
    { src: backendLayer, dest: 'backend' },
    // 'invite'/'byo' overlay the backend's pass-through accessGate with the
    // mode's variant (later layers win on path conflicts).
    ...(accessMode === 'public'
      ? []
      : [{ src: `access-${backend}-${accessMode}`, dest: 'backend' }]),
    { src: 'claude', dest: '' },
  ];
}

/** Recursively gather a layer's files keyed by their final relative path. */
function gatherLayer(
  rootTemplatesDir: string,
  layer: ScaffoldLayer,
  tokens: TemplateTokens,
  into: Map<string, ScaffoldFile>,
): void {
  const layerDir = path.join(rootTemplatesDir, layer.src);
  const walk = (absDir: string, relBase: string): void => {
    for (const entry of fs.readdirSync(absDir, { withFileTypes: true })) {
      const rel = relBase ? `${relBase}/${entry.name}` : entry.name;
      const abs = path.join(absDir, entry.name);
      if (entry.isDirectory()) {
        walk(abs, rel);
      } else if (entry.isFile()) {
        const destRel = layer.dest ? `${layer.dest}/${rel}` : rel;
        const buf = fs.readFileSync(abs);
        if (isUtf8Text(buf)) {
          into.set(destRel, {
            path: destRel,
            content: substituteString(buf.toString('utf8'), tokens),
            encoding: 'utf8',
          });
        } else {
          // Binary: never corrupt — pass bytes through as base64.
          into.set(destRel, { path: destRel, content: buf.toString('base64'), encoding: 'base64' });
        }
      }
    }
  };
  walk(layerDir, '');
}

/**
 * Assemble the layered template tree IN MEMORY and return the flat file
 * list — exactly what the CLI's on-disk scaffoldApp would have produced:
 *   - layers applied common → backend layer (into backend/) → claude overlay
 *     (later layers win on path conflicts),
 *   - {{GHOSTY_*}} tokens substituted in UTF-8 text files,
 *   - binary files returned byte-identical, encoding:'base64',
 *   - the bare "gitignore" renamed to ".gitignore",
 *   - deterministic ascending path order.
 *
 * No disk writes. Use writeScaffold() to materialise the result locally, or
 * feed the list straight into the provision manifest's `files`.
 */
export function buildScaffold(opts: ScaffoldOptions): ScaffoldFile[] {
  const rootTemplatesDir = opts.templatesDir ?? templatesDir();
  if (!fs.existsSync(rootTemplatesDir)) {
    throw new Error(
      `Templates directory not found at ${rootTemplatesDir}. ` +
        'This @ghosty/scaffolder install is incomplete — reinstall the package.',
    );
  }

  const layers = layersFor(opts.backend, opts.accessMode ?? 'public');
  const missing = layers.filter((l) => !fs.existsSync(path.join(rootTemplatesDir, l.src)));
  if (missing.length > 0) {
    throw new Error(
      `Missing template layer(s): ${missing.map((l) => l.src).join(', ')} ` +
        `(looked in ${rootTemplatesDir}).`,
    );
  }

  const merged = new Map<string, ScaffoldFile>();
  for (const layer of layers) {
    gatherLayer(rootTemplatesDir, layer, opts.tokens, merged);
  }

  // npm strips .gitignore on publish, so templates ship a bare "gitignore"
  // and the scaffold renames it. Handle root and any nested occurrence.
  for (const [key, file] of [...merged]) {
    const renamed = key === 'gitignore' ? '.gitignore' : key.replace(/\/gitignore$/, '/.gitignore');
    if (renamed !== key) {
      merged.delete(key);
      merged.set(renamed, { ...file, path: renamed });
    }
  }

  const files = [...merged.values()].sort((a, b) => a.path.localeCompare(b.path));

  // Bake the FULL brand theme into the four theme-bearing frontend files AND
  // regenerate the design docs (design-system/*.json, frontend/CLAUDE.md,
  // root DESIGN.md) so they describe the app's real theme. Runs AFTER the
  // {{GHOSTY_*}} substitution (which happens in the layered gather above), so
  // the generated docs never carry unsubstituted tokens. Deterministic
  // post-pass over the in-memory list (no disk writes). Falls back to
  // Ghosty's house brand so the scaffold is always fully branded.
  const themed = materializeDesign(files, opts.theme ?? defaultTheme(), {
    appName: opts.appConfig?.name ?? opts.tokens.GHOSTY_APP_NAME,
  });

  // Then bake the complete app.config.json from the per-app values, so the
  // committed repo (and thus the downloaded zip) carries slug/name/platform —
  // not just the template's {secrets,env} stub. Skipped for back-compat when no
  // appConfig is supplied.
  return opts.appConfig ? materializeAppConfig(themed, opts.appConfig) : themed;
}

/** Posix path of the app config file, relative to the app root. */
const APP_CONFIG_PATH = 'app.config.json';

/**
 * Deterministic post-pass: replace the template's app.config.json stub with a
 * COMPLETE, valid config built from the per-app values, preserving the stub's
 * `secrets`/`env` defaults (and any other template-provided keys). Generated
 * fields win, mirroring the CLI's writeAppConfig merge semantics. Pure —
 * returns a NEW sorted list, never mutates the input or writes to disk. When
 * app.config.json isn't present (e.g. a future backend-only scaffold) the input
 * is returned unchanged.
 */
export function materializeAppConfig(
  files: ScaffoldFile[],
  input: ScaffoldAppConfig,
): ScaffoldFile[] {
  const out = files.map((f) => {
    if (f.path !== APP_CONFIG_PATH || f.encoding !== 'utf8') return f;
    let stub: Record<string, unknown> = {};
    try {
      const parsed = JSON.parse(f.content);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        stub = parsed as Record<string, unknown>;
      }
    } catch {
      /* malformed stub — the generated config replaces it */
    }
    const { secrets, env, ...restStub } = stub;
    const merged = {
      ...restStub,
      slug: input.slug,
      name: input.name,
      description: input.description,
      database: input.database,
      fileStorage: input.fileStorage === true,
      backend: input.backend,
      accessMode: input.accessMode ?? 'public',
      secrets: Array.isArray(secrets) ? secrets : [],
      env: env && typeof env === 'object' && !Array.isArray(env) ? env : {},
      platform: { adminUrl: input.adminUrl, company: input.company },
    };
    return { ...f, content: `${JSON.stringify(merged, null, 2)}\n` };
  });
  return out.sort((a, b) => a.path.localeCompare(b.path));
}

/**
 * Materialise a scaffold file list onto disk under destDir, creating
 * directories and decoding base64 for binary files. Used by the CLI for
 * local `init`.
 */
export function writeScaffold(destDir: string, files: ScaffoldFile[]): void {
  fs.mkdirSync(destDir, { recursive: true });
  for (const file of files) {
    const abs = path.join(destDir, ...file.path.split('/'));
    fs.mkdirSync(path.dirname(abs), { recursive: true });
    const data =
      file.encoding === 'base64' ? Buffer.from(file.content, 'base64') : Buffer.from(file.content, 'utf8');
    fs.writeFileSync(abs, data);
  }
}
